import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  DesignServices as DesignServicesIcon
} from '@mui/icons-material';
import PageBuilderDialog from '../../components/Layout/PageBuilderDialog';
import AccountLayout from '../../layouts/AccountLayout';

const MicroSiteDetails = () => {
  const { microSiteId, accountId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [microSite, setMicroSite] = useState(null);
  const [pages, setPages] = useState([]);
  const [openAddPageDialog, setOpenAddPageDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [newPageData, setNewPageData] = useState({
    title: '',
    slug: ''
  });
  const [openPageBuilderDialog, setOpenPageBuilderDialog] = useState(false);
  const [addPageMenuAnchor, setAddPageMenuAnchor] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [pageBuilderOpen, setPageBuilderOpen] = useState(false);

  // Fetch microsite data
  useEffect(() => {
    const fetchMicroSite = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/api/microsites/${microSiteId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch microsite');
        }
        
        const data = await response.json();
        setMicroSite(data);
        
        // Fetch pages for this microsite
        const pagesResponse = await fetch(`/api/microsites/${microSiteId}/pages`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!pagesResponse.ok) {
          throw new Error('Failed to fetch pages');
        }
        
        const pagesData = await pagesResponse.json();
        setPages(pagesData);
        
      } catch (err) {
        console.error('Error fetching microsite:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMicroSite();
  }, [microSiteId]);

  const handleOpenAddPageDialog = () => {
    setAddPageMenuAnchor(null);
    setOpenAddPageDialog(true);
  };

  const handleCloseAddPageDialog = () => {
    setOpenAddPageDialog(false);
    setNewPageData({
      title: '',
      slug: ''
    });
  };

  const handleNewPageChange = (e) => {
    const { name, value } = e.target;
    setNewPageData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddPage = async () => {
    try {
      const response = await fetch(`/api/microsites/${microSiteId}/pages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...newPageData,
          accountId: microSite.accountId
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to create page');
      }
      
      const newPage = await response.json();
      
      // Update pages list
      setPages(prev => [...prev, newPage]);
      
      // Close dialog
      handleCloseAddPageDialog();
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Page created successfully!',
        severity: 'success'
      });
      
    } catch (err) {
      console.error('Error creating page:', err);
      setSnackbar({
        open: true,
        message: `Error creating page: ${err.message}`,
        severity: 'error'
      });
    }
  };

  const handleEditPage = (pageId) => {
    // Navigate to page edit screen
    navigate(`/account/microsites/${microSiteId}/pages/${pageId}/edit`);
  };

  const handleDeletePage = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this page?')) {
      return;
    }
    
    try {
      const response = await fetch(`/api/microsites/${microSiteId}/pages/${pageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete page');
      }
      
      // Update pages list
      setPages(prev => prev.filter(page => page.id !== pageId));
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Page deleted successfully!',
        severity: 'success'
      });
      
    } catch (err) {
      console.error('Error deleting page:', err);
      setSnackbar({
        open: true,
        message: `Error deleting page: ${err.message}`,
        severity: 'error'
      });
    }
  };

  const handleEditMicroSite = () => {
    setOpenEditDialog(true);
  };

  const handleAddPageClick = (event) => {
    setAddPageMenuAnchor(event.currentTarget);
  };
  
  const handleAddPageMenuClose = () => {
    setAddPageMenuAnchor(null);
  };
  
  const handleAddPageWithBuilder = async () => {
    handleAddPageMenuClose();
    
    try {
      // Create a new blank page first
      const response = await fetch(`/api/microsites/${microSiteId}/pages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          title: 'New Page',
          slug: `new-page-${Date.now()}`,
          content: JSON.stringify({
            content: [],
            root: {
              props: {
                style: {
                  backgroundColor: "#ffffff"
                }
              }
            }
          }),
          accountId: microSite?.accountId
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to create page');
      }
      
      const newPage = await response.json();
      
      // Navigate to the page builder for the new page
      navigate(`/account/microsites/${microSiteId}/pages/${newPage.id}/edit`);
      
    } catch (error) {
      console.error('Error creating page:', error);
      setSnackbar({
        open: true,
        message: `Error creating page: ${error.message}`,
        severity: 'error'
      });
    }
  };

  const handlePageBuilderOpen = () => {
    setPageBuilderOpen(true);
  };
  
  const handlePageBuilderClose = () => {
    setPageBuilderOpen(false);
  };

  if (loading) {
    return (
      <AccountLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </AccountLayout>
    );
  }

  if (error) {
    return (
      <AccountLayout>
        <Alert severity="error">{error}</Alert>
      </AccountLayout>
    );
  }

  return (
    <AccountLayout>
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/accounts/${accountId}/sites`)}
          sx={{ mb: 2 }}
        >
          Back to Sites
        </Button>
        
        {/* Header with back button and actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h4" component="h1">
              {microSite?.name || 'MicroSite Details'}
            </Typography>
          </Box>
          <Box>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<DesignServicesIcon />}
              onClick={() => setOpenPageBuilderDialog(true)}
              sx={{ mr: 2 }}
            >
              Page Builder
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleEditMicroSite}
            >
              Edit MicroSite
            </Button>
          </Box>
        </Box>

        {/* MicroSite Info */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>MicroSite Information</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Name</Typography>
              <Typography variant="body1">{microSite?.name}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Subdomain</Typography>
              <Typography variant="body1">{microSite?.subdomain}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Created</Typography>
              <Typography variant="body1">{new Date(microSite?.createdAt).toLocaleString()}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Last Updated</Typography>
              <Typography variant="body1">{new Date(microSite?.updatedAt).toLocaleString()}</Typography>
            </Box>
          </Box>
        </Paper>

        {/* Pages Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Pages</Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={handleAddPageClick}
            >
              Add Page
            </Button>
          </Box>

          {pages.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No pages found for this microsite.
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleOpenAddPageDialog}
                  sx={{ mr: 2 }}
                >
                  Create with Standard Editor
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DesignServicesIcon />}
                  onClick={handleAddPageWithBuilder}
                >
                  Create with Page Builder
                </Button>
              </Box>
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Slug</TableCell>
                    <TableCell>Last Updated</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pages.map((page) => (
                    <TableRow key={page.id}>
                      <TableCell>
                        {page.title}
                        {page.content && page.content.includes('"content":') && (
                          <Tooltip title="Created with Page Builder">
                            <Chip
                              icon={<DesignServicesIcon fontSize="small" />}
                              label="Page Builder"
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ ml: 1 }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>{page.slug}</TableCell>
                      <TableCell>{new Date(page.updatedAt).toLocaleString()}</TableCell>
                      <TableCell align="right">
                        <IconButton 
                          size="small" 
                          onClick={() => handleEditPage(page.id)}
                          title="Edit Page"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handlePageBuilderOpen()}
                          title="Edit with Page Builder"
                          color="primary"
                        >
                          <DesignServicesIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeletePage(page.id)}
                          title="Delete Page"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Add Page Dialog */}
        <Dialog open={openAddPageDialog} onClose={handleCloseAddPageDialog}>
          <DialogTitle>Add New Page</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              name="title"
              label="Page Title"
              type="text"
              fullWidth
              variant="outlined"
              value={newPageData.title}
              onChange={handleNewPageChange}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              name="slug"
              label="Page Slug"
              type="text"
              fullWidth
              variant="outlined"
              value={newPageData.slug}
              onChange={handleNewPageChange}
              helperText="The URL-friendly version of the title"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddPageDialog}>Cancel</Button>
            <Button onClick={handleAddPage} variant="contained" color="primary">
              Add Page
            </Button>
          </DialogActions>
        </Dialog>

        {/* Page Builder Dialog */}
        <PageBuilderDialog 
          open={pageBuilderOpen} 
          onClose={handlePageBuilderClose} 
          initialMicrosite={microSiteId}
          accountId={accountId}
        />
      </Box>
    </AccountLayout>
  );
};

export default MicroSiteDetails; 